import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { SettingsButton } from "./header-settings";
import { Banner } from "./header-banner";
import { MobileAppBanner } from "@/components/mobile-app-banner";
import { HeaderWalletButton } from "./header-wallet-button";
import { DepositButton, type DepositButtonHandle } from "./deposit-button";
import { NotificationBellButton } from "./notification-bell-button";
import { cn } from "@/lib/utils";
import { MenuIcon, FileText, HelpCircle, Mail, Sun, Moon } from "lucide-react";
import { useTheme } from "@/theme/theme-context";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/logo";
import { LayoutListContext } from "@/layout/layout-list-context";
import { primaryLayout, createLayoutWithVisibility } from "@/state/state-default";
import type { Tree } from "@/layout/layout-utils";
import { useAffiliateData } from "@/features/affiliate/hooks/use-affiliate-data";
import { useLPStatus } from "@/features/lp-dashboard/hooks/use-lp-status";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";

export const Header = () => {
  const depositButtonRef = useRef<DepositButtonHandle>(null);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { affiliate } = useAffiliateData();
  const { userId } = useUnifiedAuth();
  const { data: lpStatus } = useLPStatus(userId);
  const { theme, updateTheme } = useTheme();

  const toggleTheme = () => {
    updateTheme(theme === "dark" ? "light" : "dark");
  };

  const openDepositDialog = () => {
    depositButtonRef.current?.openDialog();
  };

  const shouldHideBanner = useMemo(() => {
    return (
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("hide_banner")
    );
  }, []);

  // Show "Affiliate Dashboard" if user is an active affiliate, otherwise show "Referrals"
  // Show "LP Dashboard" if user is an active LP provider
  const isActiveAffiliate = affiliate?.status === "active";
  const isLP = lpStatus?.isLP || false;
  
  const navItems = useMemo(() => {
    const items: Array<{ label: string; href: string; comingSoon?: boolean }> = [
      { label: "Trading", href: "/" },
      { label: "Performance Lab Pro™", href: "/performance-lab" },
      { label: "Account", href: "/account" },
      isActiveAffiliate
        ? { label: "Affiliate Dashboard", href: "/affiliate" }
        : { label: "Referrals", href: "/referrals" },
    ];
    
    // Add Leaderboards before LP Dashboard
    items.push({ label: "Leaderboards", href: "/leaderboards", comingSoon: true });
    
    // Add LP Dashboard last if user is an LP (rightmost position)
    if (isLP) {
      items.push({ label: "LP Dashboard", href: "/lp-dashboard" });
    }
    
    return items;
  }, [isActiveAffiliate, isLP]);

  return (
    <>
      {!shouldHideBanner && <Banner />}
      <MobileAppBanner />
      <header className="flex items-center gap-3 border-b px-3 py-2 max-[1000px]:px-2 max-[1000px]:py-1.5 relative z-50">
        <div className="flex items-center gap-3 sm:gap-6">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            className="min-[1001px]:hidden p-1.5 h-auto"
            onClick={() => setMobileMenuOpen(true)}
          >
            <MenuIcon className="size-5" />
          </Button>

          <a 
            href={import.meta.env.VITE_LANDING_URL || "/"} 
            className="hover:opacity-80 transition-opacity"
          >
            <Logo className="h-3 sm:h-4" />
          </a>

          {/* Desktop Navigation Menu */}
          <nav className="hidden min-[1001px]:flex items-center">
            {navItems.map((item) => {
              // Trading link is active for both "/" and "/trade/*" paths
              const isActive = item.label === "Trading" 
                ? location.pathname === "/" || location.pathname.startsWith("/trade")
                : location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.comingSoon ? "#" : item.href}
                  onClick={item.comingSoon ? (e) => e.preventDefault() : undefined}
                  className={cn(
                    "px-4 py-2.5 text-sm font-normal transition-colors relative",
                    isActive
                      ? "text-foreground"
                      : "text-foreground/80 hover:text-foreground",
                    item.comingSoon && "cursor-not-allowed opacity-50"
                  )}
                >
                  {item.label}
                  {item.comingSoon && (
                    <span className="absolute -top-0.5 -right-0.5 px-1 py-0.5 text-[8px] font-bold bg-primary text-primary-foreground rounded">
                      SOON
                    </span>
                  )}
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground" />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <DepositButton ref={depositButtonRef} />
          <HeaderWalletButton onOpenDeposit={openDepositDialog} />
          <NotificationBellButton />
          <SettingsButtonWrapper />
        </div>
      </header>

      {/* Mobile Navigation Drawer */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-[280px] p-0">
          <SheetHeader className="px-4 py-4 border-b">
            <div className="flex items-center w-full">
              <SheetTitle className="flex-shrink-0">Menu</SheetTitle>
              {/* Theme Toggle - aligned to the right */}
              <button
                onClick={toggleTheme}
                className="ml-auto p-2 rounded-full hover:bg-muted transition-colors"
                aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              >
                {theme === "dark" ? (
                  <Sun className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <Moon className="h-5 w-5 text-muted-foreground" />
                )}
              </button>
            </div>
          </SheetHeader>
          
          <div className="flex flex-col h-full">
            {/* Main Navigation */}
            <nav className="flex-1 py-2">
              {navItems.map((item) => {
                // Trading link is active for both "/" and "/trade/*" paths
                const isActive = item.label === "Trading" 
                  ? location.pathname === "/" || location.pathname.startsWith("/trade")
                  : location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.comingSoon ? "#" : item.href}
                    onClick={(e) => {
                      if (item.comingSoon) {
                        e.preventDefault();
                      } else {
                        setMobileMenuOpen(false);
                      }
                    }}
                    className={cn(
                      "flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors border-l-2",
                      isActive
                        ? "text-foreground border-foreground bg-muted/30"
                        : "text-foreground/80 hover:text-foreground hover:bg-muted/20 border-transparent",
                      item.comingSoon && "cursor-not-allowed opacity-50"
                    )}
                  >
                    <span>{item.label}</span>
                    {item.comingSoon && (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold bg-primary text-primary-foreground rounded">
                        SOON
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Divider */}
            <Separator className="my-2" />

            {/* Footer Links Section */}
            <div className="pb-4">
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Resources
                </p>
              </div>
              
              <a
                href={`${import.meta.env.VITE_SUPPORT_URL || '/support/'}contact`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors"
              >
                <HelpCircle className="h-4 w-4" />
                <span>Support & FAQ</span>
              </a>

              <a
                href={`${import.meta.env.VITE_SUPPORT_URL || '/support/'}terms`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors"
              >
                <FileText className="h-4 w-4" />
                <span>Terms of Use</span>
              </a>

              <a
                href={`${import.meta.env.VITE_SUPPORT_URL || '/support/'}privacy`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors"
              >
                <FileText className="h-4 w-4" />
                <span>Privacy Policy</span>
              </a>

              <a
                href="mailto:support@nordex.trade"
                className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors"
              >
                <Mail className="h-4 w-4" />
                <span>Contact Support</span>
              </a>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

// Wrapper component that connects SettingsButton to layout context
const SettingsButtonWrapper = () => {
  const location = useLocation();
  // Use useContext directly to check if provider is available (doesn't throw error)
  const layoutContext = useContext(LayoutListContext);

  // Only show layout controls on trading pages (/ or /trade/*) AND when context is available
  const showLayoutControls = 
    (location.pathname === "/" || location.pathname.startsWith("/trade")) && 
    layoutContext !== undefined;

  const cloneTree = (tree: Tree): Tree => {
    return JSON.parse(JSON.stringify(tree)) as Tree;
  };

  const handleResetLayout = useCallback(() => {
    if (!layoutContext) return;
    const tree = cloneTree(primaryLayout.data.tree);
    layoutContext.updateLayoutTree(layoutContext.selectedLayout.data.id, tree);
    layoutContext.setSelectedLayoutId(layoutContext.selectedLayout.data.id);
  }, [layoutContext]);

  // Regenerate layout with visibility settings passed directly from settings component
  // This avoids stale closure issues when toggling multiple panels quickly
  const handleTogglePanel = useCallback((visibility: { orderBookVisible: boolean; accountVisible: boolean }) => {
    if (!layoutContext) return;
    const tree = createLayoutWithVisibility(visibility);
    layoutContext.updateLayoutTree(layoutContext.selectedLayout.data.id, tree);
  }, [layoutContext]);

  return (
    <SettingsButton
      className="max-[1000px]:hidden"
      onResetLayout={handleResetLayout}
      onTogglePanel={handleTogglePanel}
      showLayoutControls={showLayoutControls}
    />
  );
};
